import { useEffect, useMemo, useRef } from "react";
import { useMatches } from "react-router-dom";

import { useSession } from "@/shared/model/session";
import { useDynamicTitle } from "@/shared/model/use-dynamic-title";
import { useUnread } from "@/shared/model/use-unread";

const ORIGINAL_FAVICON = "/images/icons/favicon-96x96.png";
const ANIMATION_DURATION = 1000; // 1 second for full cycle
const FRAME_RATE = 30; // frames per second

/**
 * Creates a favicon with a notification dot at given opacity
 */
function createNotificationFavicon(
  originalSrc: string,
  opacity: number,
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 96;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(originalSrc);
        return;
      }

      // Draw original favicon
      ctx.drawImage(img, 0, 0, size, size);

      // Draw notification dot (purple with opacity)
      const dotRadius = 22;
      const dotX = size - dotRadius - 2;
      const dotY = dotRadius + 2;

      // White background for better visibility
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotRadius + 2, 0, Math.PI * 2);
      ctx.fillStyle = "white";
      ctx.fill();

      // Purple dot
      ctx.beginPath();
      ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#7f67ff";
      ctx.fill();

      ctx.globalAlpha = 1;
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(originalSrc);
    img.src = originalSrc;
  });
}

/**
 * Pre-generates favicon frames for smooth animation
 */
async function generateFrames(
  originalSrc: string,
  count: number,
): Promise<string[]> {
  const frames: string[] = [];
  for (let i = 0; i < count; i++) {
    // Ease in-out sine wave for smooth pulsing
    const t = i / count;
    const opacity = Math.sin(t * Math.PI * 2) * 0.5 + 0.5;
    frames.push(await createNotificationFavicon(originalSrc, opacity));
  }
  return frames;
}

/**
 * Hook to automatically set document title based on route handle
 * Gets title from current route handle and sets it with app name suffix
 * Changes favicon when there are unread tickets with smooth pulse animation
 */
export function useAppTitle() {
  const matches = useMatches();
  const { isAuthenticated } = useSession();
  const { ticketsUnread } = useUnread();
  const { dynamicTitle } = useDynamicTitle();
  const originalFaviconRef = useRef<string | null>(null);
  const framesRef = useRef<string[]>([]);
  const animationRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frameIndexRef = useRef(0);

  const routeTitle = useMemo(() => {
    const currentMatch = matches[matches.length - 1];
    return (currentMatch?.handle as { title?: string })?.title;
  }, [matches]);

  // Store original favicon on mount
  useEffect(() => {
    const faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (faviconLink && !originalFaviconRef.current) {
      originalFaviconRef.current = faviconLink.href;
    }
  }, []);

  useEffect(() => {
    const title = dynamicTitle ?? routeTitle;
    if (!title) return;

    const hasUnread = isAuthenticated && ticketsUnread > 0;
    const fullTitle = `${title} | Monument`;
    const faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]');

    const setFavicon = (src: string) => {
      if (faviconLink) {
        faviconLink.href = src;
      }
    };

    const startAnimation = async () => {
      const originalSrc = originalFaviconRef.current || ORIGINAL_FAVICON;

      // Generate frames if not already generated
      if (framesRef.current.length === 0) {
        framesRef.current = await generateFrames(originalSrc, FRAME_RATE);
      }

      const frames = framesRef.current;
      const frameInterval = ANIMATION_DURATION / FRAME_RATE;

      animationRef.current = setInterval(() => {
        frameIndexRef.current = (frameIndexRef.current + 1) % frames.length;
        setFavicon(frames[frameIndexRef.current]);
      }, frameInterval);
    };

    const stopAnimation = () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      setFavicon(originalFaviconRef.current || ORIGINAL_FAVICON);
    };

    if (!hasUnread) {
      document.title = fullTitle;
      stopAnimation();
      return;
    }

    document.title = fullTitle;
    startAnimation();

    return () => {
      stopAnimation();
      document.title = "Monument";
    };
  }, [dynamicTitle, routeTitle, ticketsUnread, isAuthenticated]);
}
